import { useState } from 'react';
import BottomSheet from '../common/BottomSheet';
import ConfirmDialog from '../common/ConfirmDialog';
import { useApp } from '../../context/AppContext';

/**
 * "︙" action sheet for a template card (編集/削除) — opened from
 * TemplateList via AppContext's templateActionMenu.
 *
 * IMPORTANT: this must be mounted at the App-shell level (see
 * App.jsx), NOT nested inside TemplateList/TemplatesScreen. `.screen`
 * has its own transform (for the tab slide animation), which creates
 * a local stacking context — a `.sheet` (z-index 41) rendered inside
 * it can never visually escape above siblings of `.screens` like the
 * bottom nav (z-index 20), even though 41 > 20 on paper. Reading the
 * open/close state from context (rather than local state owned by
 * TemplateList) is what makes mounting it at the top level possible.
 */
export default function TemplateActionSheet() {
  const {
    templateActionMenu,
    closeTemplateActionMenu,
    openEditTemplateForm,
    removeTemplate,
    showToast,
  } = useApp();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const template = templateActionMenu;
  const menuOpen = template != null && !confirmOpen;

  const handleEdit = () => {
    if (!template) return;
    openEditTemplateForm(template);
    closeTemplateActionMenu();
  };

  const handleAskDelete = () => {
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!template || deleting) return;
    setDeleting(true);
    try {
      await removeTemplate(template.id);
      setConfirmOpen(false);
      closeTemplateActionMenu();
      showToast('テンプレートを削除しました');
    } catch (err) {
      showToast(err?.message || '削除に失敗しました。もう一度お試しください。');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setConfirmOpen(false);
    closeTemplateActionMenu();
  };

  return (
    <>
      <BottomSheet open={menuOpen} onClose={closeTemplateActionMenu} title={template?.name || 'テンプレート'}>
        <div className="template-action-row" onClick={handleEdit} role="button" tabIndex={0}>
          <span className="tar-icon">✏️</span>
          <span className="tar-label">編集</span>
          <span className="tar-chevron">›</span>
        </div>
        <div className="template-action-row danger" onClick={handleAskDelete} role="button" tabIndex={0}>
          <span className="tar-icon">🗑️</span>
          <span className="tar-label">削除</span>
          <span className="tar-chevron">›</span>
        </div>
      </BottomSheet>

      <ConfirmDialog
        open={confirmOpen}
        message={
          <>
            このテンプレートを削除しますか？
            <br />
            「{template?.name}」
          </>
        }
        confirmLabel={deleting ? '削除中…' : '削除'}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
}
